import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { joinClass } from '../lib/api';
import { DraftyMascot } from './DraftyMascot';
import { toast } from 'sonner';
import { DemoChip } from './ui/demo-chip';

const LOGO_URL = '/images/DraftFlow-billboard.png';

const JoinClass = () => {
  // Canonical link is /join/:code; /join/class/:joinCode is the legacy form.
  const { code: shortCode, joinCode } = useParams();
  const urlCode = shortCode || joinCode;
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, signInWithGoogle, signOut, profile } = useAuth();

  const [code, setCode] = useState(urlCode?.toUpperCase() || '');
  const [isJoining, setIsJoining] = useState(false);
  const [joinResult, setJoinResult] = useState(null);

  // Role mismatch (Q13): a class join link is for STUDENT accounts. If a
  // signed-in account is a teacher, we never mutate its role — we explain and
  // offer to sign in with a different account (the code is preserved).
  const roleMismatch = isAuthenticated && profile && profile.role !== 'student';

  // Sign in, persisting the code so the OAuth round-trip returns to
  // /join/<code> and auto-enrolls (without this the student lands on an empty
  // dashboard, never joined — the core of Checkpoint D finding F2).
  const signInToJoin = () => {
    const c = (urlCode || code).trim().toUpperCase();
    if (c) {
      sessionStorage.setItem('draftflow_post_login_redirect', `/join/${c}`);
    }
    signInWithGoogle('student');
  };

  // If a STUDENT arrives at /join/<code> already authenticated, auto-join.
  // Teachers (roleMismatch) are never auto-joined — they see the mismatch screen.
  useEffect(() => {
    if (urlCode && isAuthenticated && profile && !roleMismatch && !joinResult && !isJoining) {
      handleJoin(urlCode.toUpperCase());
    }
  }, [urlCode, isAuthenticated, profile, roleMismatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sign out and re-enter the join flow with a different Google account,
  // preserving the class code (Q13). No role is ever changed.
  const switchAccount = async () => {
    const c = (urlCode || code).trim().toUpperCase();
    if (c) {
      sessionStorage.setItem('draftflow_post_login_redirect', `/join/${c}`);
      sessionStorage.setItem('draftflow_intended_role', 'student');
    }
    await signOut();
    await signInWithGoogle('student', { promptSelectAccount: true });
  };

  const handleJoin = async (joinCode) => {
    const trimmed = (joinCode || code).trim().toUpperCase();
    if (!trimmed) {
      toast.error('Please enter a class code.');
      return;
    }

    setIsJoining(true);
    try {
      const result = await joinClass(trimmed);
      setJoinResult(result);
      if (result.status === 'already_joined') {
        toast.info(`You're already in ${result.class_name}.`);
      } else {
        toast.success(`Joined ${result.class_name}!`);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Invalid class code. Check with your teacher and try again.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
      console.error('Join class error:', error);
    } finally {
      setIsJoining(false);
    }
  };

  // Role mismatch (Q13) — a teacher account clicked a student join link.
  // Explain, never mutate role; one button to sign in with a different account.
  if (roleMismatch) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full text-center"
        >
          <DraftyMascot size="lg" mood="thinking" className="mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-white mb-2">This is a teacher account</h1>
          <p className="text-slate-400 mb-2">
            <span className="text-white">{profile?.email}</span> is registered on DraftFlow as a teacher.
            Class join links are for student accounts.
          </p>
          <p className="text-slate-400 mb-6">
            To join as a student, sign in with a different Google account.
          </p>
          <button
            data-testid="join-switch-account"
            onClick={switchAccount}
            className="w-full btn-teal px-6 py-3 rounded-lg font-medium"
          >
            Sign in with a different account
          </button>
          <p className="text-slate-500 text-sm mt-6">
            Just exploring? Head back and try{' '}
            <Link to="/" className="text-flow-blue-hover hover:underline">Try as Student</Link>{' '}
            demo mode from the home page.
          </p>
        </motion.div>
      </div>
    );
  }

  // Success state — joined, show confirmation
  if (joinResult) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <CheckCircle className="mx-auto mb-4 text-teal-flow" size={48} />
          <h1 className="font-heading text-3xl font-bold text-white mb-2">
            {joinResult.status === 'already_joined' ? "You're already in!" : "You're in!"}
          </h1>
          <p className="text-slate-300 text-lg mb-2">{joinResult.class_name}</p>
          <p className="text-slate-500 text-sm mb-8">
            Your teacher's assignments will appear in your dashboard.
          </p>
          <button
            data-testid="go-to-dashboard-btn"
            onClick={() => navigate('/dashboard')}
            className="btn-teal px-8 py-3 rounded-lg font-medium text-lg"
          >
            Go to Dashboard
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Header */}
      <header className="px-6 py-4 border-b border-slate-800">
        <nav className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="DraftFlow" className="h-10" />
          </Link>
          <Link
            to="/"
            data-testid="back-to-home"
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </nav>
      </header>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md w-full"
        >
          <div className="text-center mb-8">
            <DraftyMascot size="lg" mood="excited" className="mx-auto mb-4" />
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
              Join Your Class
            </h1>
            <p className="text-slate-400 mt-2">
              Enter the class code from your teacher to get started.
            </p>
          </div>

          <div className="card-surface p-6">
            {/* Code input */}
            <div className="mb-4">
              <label htmlFor="join-code" className="block text-sm text-slate-400 mb-2">
                Class Code
              </label>
              <input
                id="join-code"
                data-testid="join-code-input"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (isAuthenticated) {
                      handleJoin();
                    } else {
                      signInToJoin();
                    }
                  }
                }}
                placeholder="ABC123"
                maxLength={6}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white text-center font-mono text-2xl tracking-[0.3em] uppercase placeholder:text-slate-600 placeholder:tracking-[0.3em] focus:ring-2 focus:ring-flow-blue focus:border-transparent"
                autoFocus
              />
            </div>

            {/* Action button — depends on auth state */}
            {authLoading ? (
              <div className="flex items-center justify-center gap-2 text-slate-400 py-3">
                <Loader2 size={16} className="animate-spin" />
                Loading...
              </div>
            ) : isAuthenticated ? (
              <DemoChip label="Sign in to join a class">
                <button
                  data-testid="join-class-btn"
                  onClick={() => handleJoin()}
                  disabled={isJoining || !code.trim()}
                  className="w-full btn-teal px-6 py-3 rounded-lg font-medium text-lg flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isJoining ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Joining...
                    </>
                  ) : (
                    'Join Class'
                  )}
                </button>
              </DemoChip>
            ) : (
              <button
                data-testid="signin-to-join-btn"
                onClick={signInToJoin}
                disabled={!code.trim()}
                className="w-full flex items-center justify-center gap-3 bg-white text-slate-900 px-6 py-3 rounded-lg font-medium hover:bg-slate-100 transition-colors disabled:opacity-50"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in to Join
              </button>
            )}

            <p className="mt-4 text-xs text-slate-500 text-center">
              By signing in, you agree to our{' '}
              <Link to="/terms" className="text-slate-400 hover:text-white underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-slate-400 hover:text-white underline">Privacy Policy</Link>.
            </p>
          </div>

          {/* Teacher link */}
          <p className="text-center mt-6 text-sm text-slate-500">
            Are you a teacher?{' '}
            <Link to="/" className="text-flow-blue-hover hover:underline">
              Sign in here
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default JoinClass;
